import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { isEmpty } from 'lodash';
import { useOnboardingState } from '../../contexts/OnboardingContext';
import { validUkPostcode, validUkPhone } from '../../lib/ukFormatters';

// Using mutation for creating a new user
const CREATE_NEW_USER = gql`
  mutation createNewUser(
    $agencyName: String
    $fullName: String!
    $email: String!
    $birthDate: String!
    $password: String!
    $postal: String!
    $phone: String
    $locale: String!
    $ref: String
    $explicit_opt_in_tos_timestamp: String
    $accepted_policies: Boolean
  ) {
    createNewUser(
      agencyName: $agencyName
      fullName: $fullName
      email: $email
      birthDate: $birthDate
      password: $password
      postal: $postal
      phone: $phone
      locale: $locale
      ref: $ref
      explicit_opt_in_tos_timestamp: $explicit_opt_in_tos_timestamp
      accepted_policies: $accepted_policies
    )
  }
`;

// Create a higher-order component to connect the class component to context
const withOnboardingContext = WrappedComponent => {
  return props => {
    const context = useOnboardingState() || {};
    const currentLanguage = context.currentLanguage || props.currentLanguage || 'en';
    console.log('ContactInformationCreateUserStep language from context:', currentLanguage);
    return <WrappedComponent {...props} currentLanguage={currentLanguage} onboardingContext={context} />;
  };
};

class ContactInformationCreateUserStep extends React.Component {
  constructor(props) {
    super(props);
    
    // Get user data from resume data in window globals if available
    const resumeData = window.__WIZARD_DATA__?.resumeData || {};
    
    this.state = {
      formData: {
        name: resumeData?.name || '',
        email: resumeData?.email || '',
        phone: resumeData?.phone || '',
        zip: resumeData?.postal_code || resumeData?.zip || '',
        password: '',
        confirm_password: '',
        birth_date: resumeData?.birth_date || ''
      },
      errors: {},
      isSubmitting: false,
      openAgreement: false,
      agreedToPp: false,
      agreedToTos: false,
      showPassword: false,
      birthYear: '',
      birthMonth: '',
      birthDate: '',
      showSuccessMessage: false,
      zipValidation: { status: 'idle', city: null, state: null } // idle | validating | valid | invalid
    };

    this._zipDebounceTimer = null;

    // Bind methods
    this.handleChange = this.handleChange.bind(this);
    this.handleZipChange = this.handleZipChange.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.clickOpenAgreement = this.clickOpenAgreement.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.clickAgreePp = this.clickAgreePp.bind(this);
    this.clickAgreeTos = this.clickAgreeTos.bind(this);
    this.clickDisagreePp = this.clickDisagreePp.bind(this);
    this.clickDisagreeTos = this.clickDisagreeTos.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handleBirthDateChange = this.handleBirthDateChange.bind(this);
    this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
    this.getTranslatedText = this.getTranslatedText.bind(this);
  }

  componentWillUnmount() {
    if (this._zipDebounceTimer) clearTimeout(this._zipDebounceTimer);
  }

  componentDidUpdate(prevProps) {
    // Extract data from props if they change
    const { resumeData } = this.props;
    
    if (prevProps.resumeData !== resumeData) {
      const newFormData = {...this.state.formData};
      let hasChanges = false;
      
      if (resumeData?.name && resumeData.name !== this.state.formData.name) {
        newFormData.name = resumeData.name;
        hasChanges = true;
      }
      
      if (resumeData?.email && resumeData.email !== this.state.formData.email) {
        newFormData.email = resumeData.email;
        hasChanges = true;
      }
      
      if (resumeData?.phone && resumeData.phone !== this.state.formData.phone) {
        newFormData.phone = resumeData.phone;
        hasChanges = true;
      }
      
      if ((resumeData?.postal_code || resumeData?.zip) && 
          (resumeData.postal_code || resumeData.zip) !== this.state.formData.zip) {
        newFormData.zip = resumeData.postal_code || resumeData.zip;
        hasChanges = true;
      }
      
      if (resumeData?.birth_date && resumeData.birth_date !== this.state.formData.birth_date) {
        newFormData.birth_date = resumeData.birth_date;
        
        try {
          const date = new Date(resumeData.birth_date);
          if (!isNaN(date.getTime())) {
            this.setState({
              birthYear: date.getFullYear().toString(),
              birthMonth: (date.getMonth() + 1).toString().padStart(2, '0'),
              birthDate: date.getDate().toString().padStart(2, '0')
            });
          }
        } catch (e) {
          console.error("Error parsing birth date:", e);
        }
        
        hasChanges = true;
      }
      
      if (hasChanges) {
        this.setState({ formData: newFormData });
      }
    }
  }

  handleChange(e) {
    const { name, value } = e.target;
    if (name === 'zip') {
      this.handleZipChange(value);
      return;
    }
    this.setState({
      formData: {
        ...this.state.formData,
        [name]: value
      }
    });
  }

  handleZipChange(value) {
    this.setState({
      formData: { ...this.state.formData, zip: value },
      errors: { ...this.state.errors, zip: null },
      zipValidation: { status: 'idle', city: null, state: null }
    });

    if (this._zipDebounceTimer) clearTimeout(this._zipDebounceTimer);

    if (!value) return;

    const companyCountry = this.props.companySettings?.country || window.__WIZARD_DATA__?.companySettings?.country || '';
    const countryLower = companyCountry.toLowerCase();
    const isUS = countryLower === 'us';
    const isGB = countryLower === 'gb';

    const usZipRegex = /^\d{5}(-\d{4})?$/;
    const canadianPostalRegex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
    let formatValid;
    let invalidKey;
    if (isUS) {
      formatValid = usZipRegex.test(value);
      invalidKey = 'invalid.zip';
    } else if (isGB) {
      formatValid = validUkPostcode(value);
      invalidKey = 'invalid.uk.postcode';
    } else {
      formatValid = canadianPostalRegex.test(value);
      invalidKey = 'invalid.postal';
    }

    if (!formatValid) {
      this.setState({
        errors: { ...this.state.errors, zip: this.getTranslatedText(invalidKey) },
        zipValidation: { status: 'invalid', city: null, state: null }
      });
      return;
    }

    this.setState({ zipValidation: { status: 'validating', city: null, state: null } });

    this._zipDebounceTimer = setTimeout(() => {
      const country = isUS ? 'us' : (isGB ? 'gb' : 'ca');
      fetch(`/api/geocode/validate?postal_code=${encodeURIComponent(value)}&country=${country}`)
        .then(r => r.json())
        .then(data => {
          if (data.valid) {
            this.setState({
              zipValidation: { status: 'valid', city: data.city, state: data.state },
              errors: { ...this.state.errors, zip: null }
            });
          } else {
            const msg = isUS
              ? "We couldn't verify this zip code. Please check and try again."
              : "We couldn't verify this postal code. Please check and try again.";
            this.setState({
              zipValidation: { status: 'invalid', city: null, state: null },
              errors: { ...this.state.errors, zip: msg }
            });
          }
        })
        .catch(() => {
          this.setState({ zipValidation: { status: 'valid', city: null, state: null } });
        });
    }, 500);
  }

  handleBirthDateChange(e) {
    const { name, value } = e.target;
    this.setState({ [name]: value }, () => {
      const { birthYear, birthMonth, birthDate } = this.state;
      if (birthYear && birthMonth && birthDate) {
        // Create a date string in format YYYY-MM-DD
        const dateStr = `${birthYear}-${birthMonth}-${birthDate}`;
        
        this.setState({
          formData: {
            ...this.state.formData,
            birth_date: dateStr
          }
        });
      }
    });
  }
  
  togglePasswordVisibility() {
    this.setState(prevState => ({
      showPassword: !prevState.showPassword
    }));
  }
  
  handleCheckboxChange(e) {
    if (e.target.checked) {
      this.clickOpenAgreement();
    }
  }
  
  clickOpenAgreement() {
    this.setState({ openAgreement: true });
  }
    
  handleClose() {
    this.setState({ openAgreement: false });
  }
  
  clickAgreePp() {
    this.setState({ agreedToPp: true });
  }

  clickAgreeTos() {
    this.setState({ agreedToTos: true });
  }

  clickDisagreeTos() {
    this.setState({ agreedToTos: false });
  }

  clickDisagreePp() {
    this.setState({ agreedToPp: false });
  }
  
  stringToHTML(str) {
    return <div dangerouslySetInnerHTML={{ __html: str }} />;
  }
    
  validateForm() {
    const { formData, zipValidation } = this.state;
    const { agreedToPp, agreedToTos } = this.state;
    const newErrors = {};

    // Get company country - prioritize props over window global
    const companyCountry = this.props.companySettings?.country || window.__WIZARD_DATA__?.companySettings?.country || '';
    const countryLower = companyCountry.toLowerCase();
    const isUS = countryLower === 'us';
    const isGB = countryLower === 'gb';
    const invalidPostalKey = isUS ? 'invalid.zip' : (isGB ? 'invalid.uk.postcode' : 'invalid.postal');
    const checkPostcodeFormat = (value) => {
      if (isUS) return /^\d{5}(-\d{4})?$/.test(value);
      if (isGB) return validUkPostcode(value);
      return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(value);
    };

    if (!formData.name) newErrors.name = this.getTranslatedText('required');
    if (!formData.email) newErrors.email = this.getTranslatedText('required');

    // Validate email format
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = this.getTranslatedText('invalid.email');
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = this.getTranslatedText('required');
    } else if (formData.password.length < 8) {
      newErrors.password = this.getTranslatedText('password.length');
    }

    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = this.getTranslatedText('passwords.no.match');
    }

    // Validate ZIP/Postal code with geocode legitimacy check
    if (!formData.zip) {
      newErrors.zip = this.getTranslatedText('required');
    } else if (zipValidation.status === 'validating') {
      newErrors.zip = this.getTranslatedText('zip.validating');
    } else if (zipValidation.status === 'invalid') {
      newErrors.zip = this.state.errors.zip || this.getTranslatedText(invalidPostalKey);
    } else if (zipValidation.status === 'idle') {
      if (!checkPostcodeFormat(formData.zip)) {
        newErrors.zip = this.getTranslatedText(invalidPostalKey);
      }
    }

    if (isGB && formData.phone && !validUkPhone(formData.phone)) {
      newErrors.phone = this.getTranslatedText('invalid.uk.phone');
    }

    // Validate birth date
    if (!formData.birth_date) {
      newErrors.birth_date = this.getTranslatedText('required');
    } else {
      try {
        const date = new Date(formData.birth_date);
        if (isNaN(date.getTime())) {
          newErrors.birth_date = this.getTranslatedText('invalid.birthdate');
        }
      } catch (e) {
        newErrors.birth_date = this.getTranslatedText('invalid.birthdate');
      }
    }

    // Check if privacy policy and terms are accepted
    const optInScrollPrivacyPolicy = true; // Use prop if available
    if (optInScrollPrivacyPolicy && (!agreedToPp || !agreedToTos)) {
      newErrors.policies = this.getTranslatedText('policies.required');
    }

    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  }

  handleSubmit(e) {
    e.preventDefault();
    
    if (!this.validateForm()) return;
    
    this.setState({ isSubmitting: true });
    console.log("Form submitted, creating user...");
    
    // Prepare variables for GraphQL mutation
    const { formData } = this.state;
    const { agreedToPp, agreedToTos } = this.state;
    const { currentLanguage } = this.props;
    
    // Store data in window.__WIZARD_DATA__ for other components
    if (window.__WIZARD_DATA__) {
      window.__WIZARD_DATA__.resumeData = {
        ...window.__WIZARD_DATA__.resumeData,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        postal_code: formData.zip,
        birth_date: formData.birth_date
      };
    }
    
    // Prepare variables for GraphQL mutation
    const variables = {
      agencyName: '',
      fullName: formData.name,
      email: formData.email,
      birthDate: formData.birth_date,
      password: formData.password,
      postal: formData.zip,
      phone: formData.phone,
      locale: currentLanguage || 'en',
      ref: '',
      explicit_opt_in_tos_timestamp: (agreedToPp && agreedToTos) ? new Date().toISOString() : undefined,
      accepted_policies: agreedToPp && agreedToTos
    };
    
    console.log("Sending mutation with variables:", variables);
    
    // Execute the GraphQL mutation
    this.props.mutate({ 
      variables 
    })
    .then(response => {
      console.log("Create user response:", response);
      this.setState({ isSubmitting: false });
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        // Check if this is the special --UNAUTHORIZED-- case
        const isUnauthorizedError = response.errors.some(err => 
          err.message === '--UNAUTHORIZED--' || 
          (response.data && response.data.createNewUser === '--UNAUTHORIZED--')
        );
        
        if (isUnauthorizedError) {
          // Handle as an expected flow, not an error
          console.log("User account created and pending approval");
          this.setState({
            showSuccessMessage: true
          });
          // Hide the header when showing success message
          if (this.props.onHideHeader) {
            this.props.onHideHeader(true);
          }
          return;
        }
        
        // Handle other actual errors
        console.error("GraphQL errors during user creation:", response.errors);
        const errorMessage = response.errors.map(err => err.message).join('\n');
        this.setState({
          errors: {
            general: `Failed to create user account: ${errorMessage}`
          }
        });
        return; // Don't proceed
      }
      
      // Check if response has data and createNewUser field exists
      if (response.data && 'createNewUser' in response.data) {
        // Special case: If --UNAUTHORIZED-- was returned, show success message
        if (response.data.createNewUser === "--UNAUTHORIZED--") {
          console.log("User account created and pending approval");
          this.setState({
            showSuccessMessage: true
          });
          // Hide the header when showing success message
          if (this.props.onHideHeader) {
            this.props.onHideHeader(true);
          }
          return;
        }
        
        // Success case: createNewUser returned null
        if (response.data.createNewUser === null) {
          console.log("User created successfully (null response)");
          
          // Success - update state and proceed
          const userData = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            postal_code: formData.zip,
            birth_date: formData.birth_date,
            completed_step_1: true
          };
          
          // Update the parent component with the new user data
          if (this.props.onUserUpdate) {
            console.log("User created successfully, updating parent");
            this.props.onUserUpdate(userData);
          }
          
          // Save the current step to localStorage
          localStorage.setItem('findbob_onboarding_step', 2);
          
          // Move to the next step
          if (this.props.onNext) {
            console.log("Proceeding to next step");
            this.props.onNext();
          }
          return;
        }
        
        // Any other non-null value is an error message
        this.setState({
          errors: {
            general: response.data.createNewUser || "An error occurred during account creation"
          }
        });
      } else {
        this.setState({
          errors: {
            general: "Failed to create user account: The server did not return a valid response."
          }
        });
      }
    })
    .catch(error => {
      console.error("Error creating user:", error);
      this.setState({ 
        isSubmitting: false,
        errors: { 
          general: error.message ? error.message.replace('GraphQL error:', '') : 'An error occurred'
        }
      });
    });
  }

  // Get translated text based on current language
  getTranslatedText(key) {
    const { currentLanguage, companySettings: propsCompanySettings } = this.props;
    
    // Get company country and branded name - prioritize props over window global
    const companySettings = propsCompanySettings || window.__WIZARD_DATA__?.companySettings || {};
    const companyCountry = companySettings?.country || '';
    const companyName = companySettings?.branded_name || 'FindBob';
    const isUS = companyCountry.toLowerCase() === 'us';
    
    // Define translations
    const translations = {
      en: {
        'account.information': 'Account Information',
        'personal.information': 'Personal Information',
        'fullName': 'Full Name',
        'email': 'Email',
        'phone': 'Phone',
        'zipCode': isUS ? 'ZIP Code' : 'Postal Code',
        'password': 'Password',
        'confirm.password': 'Confirm Password',
        'birthDate': 'Date of Birth',
        'birthYear': 'Year',
        'birthMonth': 'Month',
        'birthDay': 'Date',
        'agree': 'I agree to the Terms of Service and Privacy Policy',
        'back': 'Back',
        'continue': 'Continue',
        'creating.account': 'Creating Account...',
        'min.characters': 'Minimum 8 characters',
        'required': 'This field is required',
        'policies.title': 'Privacy Policy and Terms of Service',
        'privacy.policy': 'Privacy Policy',
        'terms.service': 'Terms of Service',
        'agreed': 'Agreed',
        'not.agreed': 'Not yet agreed',
        'disagree': 'Disagree',
        'agree.button': 'Agree',
        'subtitle': `Create your account to get started with ${companyName}.`,
        'invalid.email': 'Email address is invalid',
        'password.length': 'Password must be at least 8 characters',
        'passwords.no.match': 'Passwords do not match',
        'invalid.birthdate': 'Invalid birth date',
        'policies.required': 'You must agree to the Privacy Policy and Terms of Service',
        'invalid.zip': 'Invalid ZIP code format. Must be 5 digits (12345) or ZIP+4 (12345-6789)',
        'invalid.postal': 'Invalid postal code format. Must follow the format A1A 1A1',
        'invalid.uk.postcode': 'Please enter a valid UK postcode (e.g. SW1A 1AA)',
        'invalid.uk.phone': 'Please enter a valid UK phone number (e.g. 07911 123456)',
        'zip.validating': 'Verifying your ZIP/postal code...',
        'zip.verified': 'Verified',
        'thank.you': 'Thank You!',
        'will.be.in.touch': 'We Will Be In Touch Soon.',
        'account.pending.approval': 'Your account has been created but requires approval. Someone from our team will review your information and approve your account soon. You will receive an email when your account is approved.'
      },
      fr: {
        'account.information': 'Informations du compte',
        'personal.information': 'Informations personnelles',
        'fullName': 'Nom complet',
        'email': 'Courriel',
        'phone': 'Téléphone',
        'zipCode': isUS ? 'Code ZIP' : 'Code postal',
        'password': 'Mot de passe',
        'confirm.password': 'Confirmer le mot de passe',
        'birthDate': 'Date de naissance',
        'birthYear': 'Année',
        'birthMonth': 'Mois',
        'birthDay': 'Date',
        'agree': "J'accepte les Conditions d'utilisation et la Politique de confidentialité",
        'back': 'Retour',
        'continue': 'Continuer',
        'creating.account': 'Création du compte...',
        'min.characters': 'Minimum 8 caractères',
        'required': 'Ce champ est obligatoire',
        'policies.title': "Politique de confidentialité et Conditions d'utilisation",
        'privacy.policy': 'Politique de confidentialité',
        'terms.service': "Conditions d'utilisation",
        'agreed': 'Accepté',
        'not.agreed': 'Pas encore accepté',
        'disagree': 'Refuser',
        'agree.button': 'Accepter',
        'subtitle': `Créez votre compte pour commencer avec ${companyName}.`,
        'invalid.email': 'Courriel est invalide',
        'password.length': 'Mot de passe doit comporter au moins 8 caractères',
        'passwords.no.match': 'Les mots de passe ne correspondent pas',
        'invalid.birthdate': 'Date de naissance invalide',
        'policies.required': "Vous devez accepter la Politique de confidentialité et les Conditions d'utilisation",
        'invalid.zip': 'Format de code ZIP invalide. Doit être 5 chiffres (12345) ou ZIP+4 (12345-6789)',
        'invalid.postal': 'Format de code postal invalide. Doit suivre le format A1A 1A1',
        'invalid.uk.postcode': 'Veuillez saisir un code postal britannique valide (par ex. SW1A 1AA)',
        'invalid.uk.phone': 'Veuillez saisir un numéro de téléphone britannique valide (par ex. 07911 123456)',
        'zip.validating': 'Vérification du code postal en cours...',
        'zip.verified': 'Vérifié',
        'thank.you': 'Merci!',
        'will.be.in.touch': 'Nous vous contacterons bientôt.',
        'account.pending.approval': 'Votre compte a été créé mais nécessite une approbation. Quelqu\'un de notre équipe examinera vos informations et approuvera votre compte bientôt. Vous recevrez un courriel lorsque votre compte sera approuvé.'
      }
    };
    
    // Return translated text or default to English
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
  }

  render() {
    const {
      formData,
      errors,
      isSubmitting,
      openAgreement,
      agreedToPp,
      agreedToTos,
      showPassword,
      birthYear,
      birthMonth,
      birthDate,
      showSuccessMessage,
      zipValidation
    } = this.state;

    const loading = this.props.loading;
    const { privacyPolicy, termsOfService, companySettings } = this.props;
    const useBirthYear = companySettings?.use_birth_year_instead_of_dob || window.__WIZARD_DATA__?.companySettings?.use_birth_year_instead_of_dob || false;
    const zipIsBlocking = formData.zip && (zipValidation.status === 'validating' || zipValidation.status === 'invalid');
    
    // Show success message if account was created and pending approval
    if (showSuccessMessage) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {this.getTranslatedText('thank.you')}
            </h2>
            <h3 className="text-xl font-medium text-gray-900 mb-6">
              {this.getTranslatedText('will.be.in.touch')}
            </h3>
            <p className="text-gray-600 leading-relaxed max-w-md mx-auto">
              {this.getTranslatedText('account.pending.approval')}
            </p>
          </div>
        </div>
      );
    }
    
    // Get company country for placeholder and validation - prioritize props over window global
    const companyCountry = companySettings?.country || window.__WIZARD_DATA__?.companySettings?.country || '';
    const countryLower = companyCountry.toLowerCase();
    const isUS = countryLower === 'us';
    const isGB = countryLower === 'gb';
    const zipPlaceholder = isUS ? "12345" : (isGB ? "SW1A 1AA" : "A1A 1A1");
    const phonePlaceholder = isGB ? "07911 123456" : undefined;
    
    // Generate years for dropdown (100 years ago to 16 years ago)
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentYear - 16 - i);
    
    // Generate months for dropdown
    const months = Array.from({ length: 12 }, (_, i) => {
      const num = i + 1;
      return { value: num.toString().padStart(2, '0'), label: new Date(2000, i, 1).toLocaleString('default', { month: 'long' }) };
    });
    
    // Generate days for dropdown
    const days = Array.from({ length: 31 }, (_, i) => {
      const num = i + 1;
      return num.toString().padStart(2, '0');
    });
  
    return (
      <div className="bg-succession-bg-primary rounded-lg p-8 shadow-succession-md" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
        {errors.general && !showSuccessMessage && (
          <div className="mb-6 p-4 bg-succession-error-bg border border-red-200 rounded-lg text-sm text-succession-error shadow-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-succession-error mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
              </svg>
              {errors.general}
            </div>
          </div>
        )}
        
        <form onSubmit={this.handleSubmit} className="space-y-6">
          <div className="space-y-6">
            <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6 shadow-succession-sm" style={{ backgroundColor: 'var(--succession-bg-secondary, #f8fafc)' }}>
              <h3 className="text-lg font-medium text-succession-text-primary mb-4 pb-2 border-b border-succession-border">{this.getTranslatedText('account.information')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{this.getTranslatedText('fullName')} <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={this.handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.name ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                    style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                    placeholder={this.getTranslatedText('fullName')}
                  />
                  {errors.name && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.name}</div>}
                </div>
              
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{this.getTranslatedText('email')} <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={this.handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.email ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                    style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                    placeholder="you@example.com"
                  />
                  {errors.email && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.email}</div>}
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">{this.getTranslatedText('password')} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={this.handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.password ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                      placeholder={this.getTranslatedText('min.characters')}
                    />
                    <button
                      type="button"
                      onClick={this.togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.password}</div>}
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">{this.getTranslatedText('confirm.password')} <span className="text-red-500">*</span></label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirm_password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={this.handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.confirm_password ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                    placeholder={this.getTranslatedText('confirm.password')}
                  />
                  {errors.confirm_password && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.confirm_password}</div>}
                </div>
              </div>
            </div>
            
            <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6 shadow-succession-sm" style={{ backgroundColor: 'var(--succession-bg-secondary, #f8fafc)' }}>
              <h3 className="text-lg font-medium text-succession-text-primary mb-4 pb-2 border-b border-succession-border">{this.getTranslatedText('personal.information')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={useBirthYear ? "md:col-span-2" : "md:col-span-2"}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {this.getTranslatedText('birthDate')} <span className="text-red-500">*</span>
                  </label>
                  
                  <div className="flex space-x-3">
                    <div className="w-1/3">
                      <select
                        name="birthMonth"
                        value={birthMonth}
                        onChange={this.handleBirthDateChange}
                        className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.birth_date ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                      >
                        <option value="">{this.getTranslatedText('birthMonth')}</option>
                        {months.map(month => (
                          <option key={month.value} value={month.value}>{month.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="w-1/3">
                      <select
                        name="birthDate"
                        value={birthDate}
                        onChange={this.handleBirthDateChange}
                        className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.birth_date ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                      >
                        <option value="">{this.getTranslatedText('birthDay')}</option>
                        {days.map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="w-1/3">
                      <select
                        name="birthYear"
                        value={birthYear}
                        onChange={this.handleBirthDateChange}
                        className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.birth_date ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                      >
                        <option value="">{this.getTranslatedText('birthYear')}</option>
                        {years.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {errors.birth_date && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.birth_date}</div>}
                </div>
              
                <div>
                  <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">{this.getTranslatedText('zipCode')} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={this.handleChange}
                      className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors pr-10 ${errors.zip ? 'border-succession-error bg-succession-error-bg' : zipValidation.status === 'valid' ? 'border-green-500' : 'border-succession-border'}`}
                      style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                      placeholder={zipPlaceholder}
                    />
                    {zipValidation.status === 'validating' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                        <svg className="animate-spin h-4 w-4 text-succession-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                    {zipValidation.status === 'valid' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                        <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {zipValidation.status === 'valid' && (zipValidation.city || zipValidation.state) && (
                    <div className="mt-1.5 text-sm text-green-600 flex items-center">
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                      {[zipValidation.city, zipValidation.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {errors.zip && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" /></svg>{errors.zip}</div>}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">{this.getTranslatedText('phone')}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={this.handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg shadow-sm bg-succession-bg-primary text-succession-text-primary focus:ring-succession-primary focus:border-succession-primary transition-colors ${errors.phone ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                    placeholder={phonePlaceholder || "(123) 456-7890"}
                  />
                  {errors.phone && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.phone}</div>}
                </div>
              </div>
            </div>
          </div>
          
          {/* Privacy Policy and Terms of Service Checkbox */}
          <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6 shadow-succession-sm" style={{ backgroundColor: 'var(--succession-bg-secondary, #f8fafc)' }}>
            <div className="flex items-center">
              <input
                id="policies"
                name="policies"
                type="checkbox"
                checked={agreedToPp && agreedToTos}
                onChange={this.handleCheckboxChange}
                className="h-5 w-5 text-succession-primary focus:ring-succession-primary border-succession-border rounded transition-colors"
              />
              <label htmlFor="policies" className="ml-3 block text-sm text-succession-text-primary font-medium">
                {this.getTranslatedText('agree')}
              </label>
            </div>
            {errors.policies && (
              <div className="mt-2 text-sm text-succession-error flex items-center pl-8">
                <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
                </svg>
                {errors.policies}
              </div>
            )}
          </div>
          
          <div className="flex justify-end pt-4">
            <button 
              type="submit" 
              className="inline-flex justify-center items-center py-2.5 px-8 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all hover:-translate-y-0.5"
              style={{ 
                backgroundColor: 'var(--succession-primary)',
                '&:hover': {
                  backgroundColor: 'var(--succession-primary-hover)'
                }
              }}
              disabled={isSubmitting || loading || zipIsBlocking}
            >
              {isSubmitting || loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {this.getTranslatedText('creating.account')}
                </>
              ) : (
                <>
                  {this.getTranslatedText('continue')}
                  <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
        
        {/* Policies Dialog */}
        {openAgreement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 p-4 overflow-y-auto" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="min-h-full flex items-start justify-center py-8">
              <div className="bg-succession-bg-primary rounded-xl shadow-succession-lg max-w-3xl w-full flex flex-col max-h-[calc(100vh-4rem)]" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
                {/* Fixed Header */}
                <div className="flex justify-between items-center p-6 border-b border-succession-border flex-shrink-0" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
                  <h3 className="text-xl font-semibold text-succession-text-primary">{this.getTranslatedText('policies.title')}</h3>
                  <button 
                    onClick={this.handleClose}
                    className="text-succession-text-muted hover:text-succession-text-primary transition-colors p-1 rounded-full hover:bg-succession-hover-bg"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between items-center cursor-pointer bg-succession-bg-secondary p-4 rounded-lg mb-3" style={{ backgroundColor: 'var(--succession-bg-secondary, #f8fafc)' }}>
                        <h4 className="font-medium text-succession-text-primary">{this.getTranslatedText('privacy.policy')} {agreedToPp ? <span className="text-succession-success ml-2 text-sm bg-succession-success-bg py-0.5 px-2 rounded-full">{this.getTranslatedText('agreed')}</span> : <span className="text-succession-text-muted ml-2 text-sm bg-succession-bg-secondary py-0.5 px-2 rounded-full">{this.getTranslatedText('not.agreed')}</span>}</h4>
                      </div>
                      <div className="p-4 border border-succession-border rounded-lg max-h-60 overflow-y-auto bg-succession-bg-primary shadow-inner text-sm text-succession-text-primary" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
                        {this.stringToHTML(privacyPolicy || '')}
                      </div>
                      <div className="mt-4 flex justify-end space-x-3">
                        <button 
                          onClick={this.clickDisagreePp}
                          className="py-2 px-4 bg-succession-bg-secondary text-succession-text-secondary rounded-lg hover:bg-succession-hover-bg transition-colors"
                        >
                          {this.getTranslatedText('disagree')}
                        </button>
                        <button 
                          onClick={this.clickAgreePp}
                          className="py-2 px-4 bg-succession-primary text-white rounded-lg hover:bg-succession-primary-hover transition-colors"
                          style={{ backgroundColor: 'var(--succession-primary)' }}
                        >
                          {this.getTranslatedText('agree.button')}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center cursor-pointer bg-succession-bg-secondary p-4 rounded-lg mb-3" style={{ backgroundColor: 'var(--succession-bg-secondary, #f8fafc)' }}>
                        <h4 className="font-medium text-succession-text-primary">{this.getTranslatedText('terms.service')} {agreedToTos ? <span className="text-succession-success ml-2 text-sm bg-succession-success-bg py-0.5 px-2 rounded-full">{this.getTranslatedText('agreed')}</span> : <span className="text-succession-text-muted ml-2 text-sm bg-succession-bg-secondary py-0.5 px-2 rounded-full">{this.getTranslatedText('not.agreed')}</span>}</h4>
                      </div>
                      <div className="p-4 border border-succession-border rounded-lg max-h-60 overflow-y-auto bg-succession-bg-primary shadow-inner text-sm text-succession-text-primary" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
                        {this.stringToHTML(termsOfService || '')}
                      </div>
                      <div className="mt-4 flex justify-end space-x-3">
                        <button 
                          onClick={this.clickDisagreeTos}
                          className="py-2 px-4 bg-succession-bg-secondary text-succession-text-secondary rounded-lg hover:bg-succession-hover-bg transition-colors"
                        >
                          {this.getTranslatedText('disagree')}
                        </button>
                        <button 
                          onClick={this.clickAgreeTos}
                          className="py-2 px-4 bg-succession-primary text-white rounded-lg hover:bg-succession-primary-hover transition-colors"
                          style={{ backgroundColor: 'var(--succession-primary)' }}
                        >
                          {this.getTranslatedText('agree.button')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Fixed Footer */}
                <div className="flex justify-end p-6 border-t border-succession-border flex-shrink-0" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
                  <button 
                    onClick={this.handleClose}
                    className="py-2.5 px-6 bg-succession-primary text-white rounded-lg hover:bg-succession-primary-hover transition-colors flex items-center"
                    style={{ backgroundColor: 'var(--succession-primary)' }}
                    disabled={!agreedToPp || !agreedToTos}
                  >
                    {this.getTranslatedText('continue')}
                    <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

ContactInformationCreateUserStep.propTypes = {
  onNext: PropTypes.func,
  onUserUpdate: PropTypes.func,
  mutate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onBack: PropTypes.func,
  privacyPolicy: PropTypes.string,
  termsOfService: PropTypes.string,
  resumeData: PropTypes.object,
  currentLanguage: PropTypes.string,
  onHideHeader: PropTypes.func,
  companySettings: PropTypes.object
};

ContactInformationCreateUserStep.defaultProps = {
  currentLanguage: 'en'
};

// Wrap component with the GraphQL mutation
export default graphql(CREATE_NEW_USER, {
  options: { 
    errorPolicy: 'all' 
  }
})(withOnboardingContext(ContactInformationCreateUserStep)); 